class DirtyForm {
  static DEFAULT_MESSAGE = "You have unsaved changes. Are you sure you want to continue"

  constructor(form, options = {}) {
    this.form = form
    this.isDirty = false
    this.initialValues = {}
    this.fields = [
      ...this.form.elements,
      ...this.form.querySelectorAll("trix-editor")
    ]
    this.message = options["message"] || DirtyForm.DEFAULT_MESSAGE

    // Bind once so tearDown can remove the exact same listener references.
    this.checkValue = this.checkValue.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleReset = this.handleReset.bind(this)
    this.windowUnload = this.windowUnload.bind(this)
    this.cancelOrConfirm = this.cancelOrConfirm.bind(this)

    this.setupFields()
    this.setFormHandlers()
  }

  // isDirty stays the source of truth; mirror it on the form element so
  // code without a handle on this instance (base_modal's dismissal
  // confirm) can see it in the DOM.
  get isDirty() {
    return this._isDirty
  }

  set isDirty(value) {
    this._isDirty = !!value
    this.form?.toggleAttribute("data-dirty", this._isDirty)
  }

  setupFields() {
    // Trix is lazy-loaded, so a <trix-editor> may not be upgraded yet: it has
    // no name or value until it initializes. Take its baseline then.
    this.form.querySelectorAll("trix-editor").forEach(field => {
      const saveInitialValue = () => { this.initialValues[field.name] = field.value }

      field.editor ? saveInitialValue() : field.addEventListener("trix-initialize", saveInitialValue, { once: true })
      field.addEventListener("trix-change", this.checkValue)
    })

    this.trackedFields.forEach(field => {
      if (field.nodeName == "TRIX-EDITOR") { return }

      // Save initial values
      this.initialValues[field.name] = field.value

      // Set handlers
      field.addEventListener("change", this.checkValue)
      field.addEventListener("input", this.checkValue)
    })
  }

  get trackedFields() {
    return this.fields.filter(field =>
      field.name && field.type != "submit" && field.type != "button" && field.type != "hidden"
    )
  }

  windowUnload(event) {
    if (this.isDirty) {
      event.preventDefault()
      event.returnValue = this.message
    }
  }

  cancelOrConfirm(event) {
    if (this.formSurvivesVisit(event)) { return }

    if (this.isDirty && !confirm(this.message)) {
      event.preventDefault()
    } else {
      this.isDirty = false
    }
  }

  // A same-URL visit on a page that refreshes with morph is a page refresh
  // (Turbo 8 renders it with idiomorph), and a form inside a shown modal
  // rides it out untouched — open modals are data-turbo-permanent (see
  // setup/modal_morph_persistence.js), so a background refresh discards
  // nothing. Prompting would interrupt the user for a non-event, and the
  // else-branch above would wrongly mark the still-edited form clean.
  formSurvivesVisit(event) {
    if (!this.form.closest(".modal.show")) { return false }
    if (event.detail?.url !== window.location.href) { return false }

    return document.querySelector("meta[name='turbo-refresh-method']")?.content === "morph"
  }

  setFormHandlers() {
    // Handle submit
    this.form.addEventListener("submit", this.handleSubmit)

    // form.reset() restores the fields without firing input/change events,
    // but it does fire a reset event (HTML spec) — and a reset form is
    // clean. base_modal.js resets forms when a modal is dismissed; without
    // this, isDirty would stay true forever and prompt on every later
    // navigation even though the modal is closed.
    this.form.addEventListener("reset", this.handleReset)

    // Handle leaving page. A per-instance listener (not window.onbeforeunload
    // assignment) so multiple dirty forms on one page don't clobber each
    // other's handlers — any dirty instance blocks the unload.
    window.addEventListener("beforeunload", this.windowUnload)

    document.addEventListener("turbo:before-visit", this.cancelOrConfirm)
  }

  // Recompute dirtiness from scratch instead of latching it. Widget resets
  // that run after form.reset() fire native change events (slim-select's
  // MutationObserver dispatches change from a microtask when it re-renders
  // the option nodes, after its changeListen(false) guard is already back
  // on) — a latch would re-mark the just-reset form dirty forever. Deriving
  // means any churn converges to the true state, and a user who manually
  // undoes their edit is no longer prompted either.
  checkValue() {
    this.isDirty = this.trackedFields.some(field =>
      field.isConnected && field.name in this.initialValues && this.initialValues[field.name] != field.value
    )
  }

  handleSubmit() {
    this.isDirty = false
  }

  // The reset event fires BEFORE the fields are restored (HTML spec), so
  // recomputing here would still read the edited values — a reset form is
  // clean by definition, so clear the flag explicitly.
  handleReset() {
    this.isDirty = false
  }

  tearDown() {
    this.isDirty = false
    this.initialValues = {}

    this.trackedFields.forEach(field => {
      field.removeEventListener("trix-change", this.checkValue)
      field.removeEventListener("change", this.checkValue)
      field.removeEventListener("input", this.checkValue)
    })

    this.form.removeEventListener("submit", this.handleSubmit)
    this.form.removeEventListener("reset", this.handleReset)

    window.removeEventListener("beforeunload", this.windowUnload)

    document.removeEventListener("turbo:before-visit", this.cancelOrConfirm)
  }
}

module.exports = DirtyForm
